import dayjs from "dayjs";
import { useMemo } from "react";
import styled from "styled-components";
import {
  Body,
  Caption1,
  Footnote,
  Headline,
  Subhead,
} from "../../components/Typography";
import { DropEntry } from "../../dropTypes";
import useCountdown from "../../hooks/countdown";
import { hideAboveMobile, hideOnMobile } from "../../lib/mediaQuery";

interface Props {
  drop: DropEntry;
  isPending?: boolean;
}

export const ThumbnailStatus = ({ drop, isPending = false }: Props) => {
  const dropTimestamp = Number(drop?.data?.startDate);
  const dropCountdown = useCountdown(dropTimestamp);
  let showDropTime = !(
    dropCountdown.isComplete ||
    isNaN(dropCountdown.seconds) ||
    isNaN(dropCountdown.minutes) ||
    isNaN(dropCountdown.hours) ||
    isNaN(dropCountdown.days)
  );

  const isSoldOut = drop.variants[0]?.itemsRemaining === 0;
  const isEnded = drop.state?.kind === "Ended";

  if (isPending) {
    return (
      <ThumbnailStatusContainer visible={Boolean(showDropTime)}>
        <ThumbnailPending />
      </ThumbnailStatusContainer>
    );
  }

  if (isSoldOut) {
    return (
      <ThumbnailStatusContainer visible={Boolean(showDropTime)}>
        <ThumbnailSoldOut />
      </ThumbnailStatusContainer>
    );
  }

  if (isEnded) {
    return (
      <ThumbnailStatusContainer visible={Boolean(showDropTime)}>
        <ThumbnailEnded />
      </ThumbnailStatusContainer>
    );
  }

  return (
    <>
      {showDropTime && (
        <ThumbnailStatusContainer visible={Boolean(showDropTime)}>
          <ThumbnailDropTime dropTime={dropTimestamp} />
        </ThumbnailStatusContainer>
      )}
    </>
  );
};

const COUNTDOWN_MIN_DAYS = 1;

interface DropTimeProps {
  className?: string;
  dropTime: number;
}

const ThumbnailDropTime: React.FC<DropTimeProps> = ({ dropTime }) => {
  const dropDate = useMemo(() => dayjs(dropTime * 1000), [dropTime]);
  const dropCountdown = useCountdown(dropTime);
  const showDateDrop = dropCountdown.days >= COUNTDOWN_MIN_DAYS;

  return (
    <>
      <Body css={hideOnMobile}>Dropping:</Body>
      <Footnote css={hideAboveMobile}>Dropping:</Footnote>
      {showDateDrop ? (
        <>
          <Body css={hideOnMobile}>
            {dropDate.isValid() && dropDate.format("DD MMM YYYY")}
          </Body>
          <Footnote css={hideAboveMobile}>
            {dropDate.isValid() && dropDate.format("DD MMM YYYY")}
          </Footnote>
        </>
      ) : (
        <>
          <Body css={hideOnMobile}>
            {dropDate.isValid() &&
              `${pad(dropCountdown.hours)} : ${pad(
                dropCountdown.minutes
              )} : ${pad(dropCountdown.seconds)}`}
          </Body>
          <Footnote css={hideAboveMobile}>
            {dropDate.isValid() &&
              `${pad(dropCountdown.hours)} : ${pad(
                dropCountdown.minutes
              )} : ${pad(dropCountdown.seconds)}`}
          </Footnote>
        </>
      )}
    </>
  );
};

function pad(value: number, length: number = 2, char: string = "0") {
  return String(value).padStart(length, char);
}

export const ThumbnailSoldOut = () => {
  return (
    <div
      css={`
        width: 100%;
        text-align: center;
      `}
    >
      <Headline
        css={`
          color: rgb(var(--magenta-mid));
          ${hideOnMobile}
        `}
      >
        SOLD OUT
      </Headline>
      <Subhead
        css={`
          color: rgb(var(--magenta-mid));
          ${hideAboveMobile}
        `}
      >
        SOLD OUT
      </Subhead>
    </div>
  );
};

export const ThumbnailPending = () => {
  return (
    <ThumbnailStatusContainer>
      <Subhead
        css={`
          width: 100%;
          text-align: center;
          ${hideOnMobile};
        `}
      >
        Pending...
      </Subhead>
      <Caption1
        css={`
          width: 100%;
          text-align: center;
          ${hideAboveMobile}
        `}
      >
        Pending...
      </Caption1>
    </ThumbnailStatusContainer>
  );
};

export const ThumbnailEnded = () => {
  return (
    <div
      css={`
        width: 100%;
        text-align: center;
      `}
    >
      <Headline
        css={`
          color: rgb(var(--magenta-mid));
          ${hideOnMobile}
        `}
      >
        Offer Ended
      </Headline>
      <Subhead
        css={`
          color: rgb(var(--magenta-mid));
          ${hideAboveMobile}
        `}
      >
        Offer Ended
      </Subhead>
    </div>
  );
};

const ThumbnailStatusContainer = styled.div<{ visible?: Boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 1;
  padding: var(--spacing-xs);
  background: rgba(1, 1, 1, 0.75);
  backdrop-filter: blur(7px);
  z-index: var(--depth-high);
`;
